// Telehealth v2 — pure helpers (dates, status signals, grouping). No React.
import { api } from "@/lib/api";
import { displayName } from "@/lib/patientFormat";
import { toast } from "sonner";

export const B = "/api/edi/televisit";
export const OPEN = ["scheduled", "confirmed", "waiting_room", "in_progress"];
export const CLOSED = ["completed", "cancelled", "canceled", "no_show"];
export const isOpen = (a) => OPEN.includes(a.status);
export const AMBER = "#9A5A00", INDIGO = "#5856D6", GREEN = "#1F7A46", FAINT = "#9B9B97", INK = "#141412", GREY2 = "#6E6E6B", HAIR = "#E9E9EC";

// §11.1: chart patients → placeholder (no request, no tab); no-chart → the app shell opens the visit screen.
// Event-based so it survives a module reload (a module-level handler was lost on reload and Start did nothing).
export const setSessionOpener = (fn) => {
  if (!fn) return;
  const on = (e) => fn(e.detail);
  window.addEventListener("revq:th-start", on);
  return () => window.removeEventListener("revq:th-start", on);
};
export function openTelehealthSession(appointment) {
  window.dispatchEvent(new CustomEvent("revq:th-start", { detail: appointment }));
}
// Soft guard: starting a visit booked for another day asks once before opening it.
export function startVisitChecked(a) {
  const d = new Date(a.scheduled_at);
  if (dayKey(d) === dayKey(new Date())) return openTelehealthSession(a);
  toast.warning(`${displayName(a.patient?.name || "This patient")}'s visit is for ${fmtDayHead(d)} at ${fmtTime(d)}, not today.`, { id: `th-future-${a.appointment_id}`, duration: 7000, action: { label: "Start anyway", onClick: () => openTelehealthSession(a) } });
}
export const futureDayNote = (d) => { const t = startOfDay(new Date()), n = Math.round((startOfDay(d) - t) / 864e5); return n === 0 ? null : n === 1 ? `tomorrow, ${fmtDayHead(d)}` : fmtDayHead(d); };
export const startTip = (a) => (chartOf(a) ? "Opens this visit in the patient's chart" : "Opens the visit screen");

const pad = (n) => String(n).padStart(2, "0");
export const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const at = (a) => new Date(a.scheduled_at);
export const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
export const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const fmtTime = (d) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
export const fmtMD = (d) => d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
export const fmtDayHead = (d) => d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
export const naiveIso = (d) => `${dayKey(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
export const patientName = (a) => displayName(a.patient?.name || a.patient_name || "Patient");
export const firstName = (a) => patientName(a).split(" ")[0];
export const chartOf = (a) => a.patient?.chart_uuid || a.patient_chart_uuid || null;
// Patient join link always points at the EHI host (same as the original Telemedicine invite), never this app's own URL.
const EHI_HOST = "https://ehi.ariaone.ai";
export const linkOf = (a) => {
  const p = a.patient_link_path || (a.patient_link_token ? `/edi/televisit/join/${a.patient_link_token}` : "");
  if (!p) return a.room_url || "";
  return p.startsWith("http") ? p : `${EHI_HOST}${p.startsWith("/") ? "" : "/"}${p}`;
};

export const RANGES = [["today", "Today"], ["week", "This week"], ["next7", "Next 7 days"], ["last7", "Last 7 days"], ["last30", "Last 30 days"]];
export function rangeDates(range, now = new Date()) {
  const t = startOfDay(now);
  if (range === "today") return [t, t];
  if (range === "next7") return [t, addDays(t, 6)];
  if (range === "last7") return [addDays(t, -6), t];
  if (range === "last30") return [addDays(t, -29), t];
  const s = addDays(t, -t.getDay());
  return [s, addDays(s, 6)];
}

export const SEGMENTS = [["open", "Open"], ["waiting", "Waiting room"], ["completed", "Completed"], ["cancelled", "Cancelled & no-show"], ["all", "All"]];
export const isWaiting = (a) => a.status === "waiting_room" || (a.patient_waiting && isOpen(a));
export function inSegment(a, seg) {
  if (seg === "open") return isOpen(a);
  if (seg === "waiting") return isWaiting(a);
  if (seg === "completed") return a.status === "completed";
  if (seg === "cancelled") return a.status === "cancelled" || a.status === "canceled" || a.status === "no_show";
  return true;
}
export const isStuck = (a, now = new Date()) => a.status === "in_progress" && a.start_time && startOfDay(new Date(a.start_time)) < startOfDay(now);
export const needsYou = (a) => isWaiting(a) || isStuck(a);

// §3 signal: dot style + words for line 1.
export function signal(a, waitMin) {
  if (isWaiting(a)) return { dot: "amber", words: waitMin != null ? `Waiting · ${waitMin} min` : "Waiting", tone: "amber" };
  if (a.status === "in_progress") return isStuck(a) ? { dot: "amber", words: `In progress since ${fmtMD(new Date(a.start_time))}`, tone: "amber" } : { dot: "pulse", words: "In progress", tone: "ink" };
  if (a.status === "confirmed") return { dot: "indigo" };
  if (a.status === "completed") return { dot: "green", words: "Completed", tone: "faint" };
  if (a.status === "no_show") return { dot: "grey", words: "No-show", tone: "faint" };
  if (a.status === "cancelled" || a.status === "canceled") return { dot: "grey", words: "Cancelled", tone: "faint" };
  return { dot: "ring" };
}
export function intakeText(a) {
  if (a.intake_completed) return "Intake done";
  if (a.intake_answered_count > 0) return `Intake · ${a.intake_answered_count} answered`;
  return "Intake pending";
}

export function daySummary(list) {
  return {
    need: list.filter(needsYou).length,
    open: list.filter((a) => isOpen(a) && !needsYou(a)).length,
    done: list.filter((a) => a.status === "completed").length,
    gone: list.filter((a) => !isOpen(a) && a.status !== "completed").length,
  };
}

// Today first, future ascending, past descending.
export function groupDays(list, now = new Date()) {
  const today = dayKey(now), map = new Map();
  [...list].sort((x, y) => at(x) - at(y)).forEach((a) => { const k = dayKey(at(a)); if (!map.has(k)) map.set(k, []); map.get(k).push(a); });
  const keys = [...map.keys()];
  const fut = keys.filter((k) => k > today).sort(), past = keys.filter((k) => k < today).sort().reverse();
  return [...(map.has(today) ? [today] : []), ...fut, ...past].map((k) => ({ key: k, date: new Date(`${k}T00:00:00`), items: map.get(k), isToday: k === today }));
}

export async function fetchAppointments({ cid, range, npi, q, from, to }) {
  const [s, e] = from ? [from, to] : rangeDates(range);
  const params = { client_id: String(cid), date_from: dayKey(s), date_to: `${dayKey(e)}T23:59:59`, limit: 500 };
  if (npi) params.npi = npi;
  if (q) params.q = q;
  return (await api.get(`${B}/appointments`, { params })).data?.appointments ?? [];
}
export async function fetchSlots(cid, npi, date) {
  const { data } = await api.get(`${B}/slots/available`, { params: { client_id: String(cid), npi, date: dayKey(date) } });
  return { slots: (data?.slots || []).filter((s) => s.available), slot: data?.slot_duration || 30 };
}
// A slot stays bookable until it ends (start + duration), so a slot already under way can still be booked.
export const openSlots = (r) => (r?.slots || []).filter((s) => new Date(s.iso).getTime() + (r.slot || 30) * 60000 > Date.now());
export const slotStarted = (s) => new Date(s.iso) <= new Date();
export const patchAppt = async (id, body) => (await api.patch(`${B}/appointments/${id}`, body)).data;
export const deleteAppt = async (id) => (await api.delete(`${B}/appointments/${id}`)).data;
export const getPatientMedical = async (id) => (await api.get(`${B}/appointments/${id}/patient-medical`)).data?.context || null;

export const DAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
export const DLABEL = { sun: "Sun", mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat" };
const hm = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
const h12 = (min) => { const h = Math.floor(min / 60) % 24, m = min % 60, hh = ((h + 11) % 12) + 1; return m ? `${hh}:${pad(m)}` : `${hh}`; };
export const toMin = hm;
export const winText = (ws) => (ws?.length ? ws.map((w) => `${h12(hm(w.start))}–${h12(hm(w.end))}${hm(w.start) >= 18 * 60 ? " PM" : ""}`).join(", ") : "Off");
export function hoursSummary(cfg) {
  if (!cfg?.weekly_hours) return "No hours set";
  const order = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"], parts = [];
  let i = 0;
  while (i < order.length) {
    const t = winText(cfg.weekly_hours[order[i]]); let j = i;
    while (j + 1 < order.length && winText(cfg.weekly_hours[order[j + 1]]) === t) j += 1;
    if (t !== "Off") parts.push(`${DLABEL[order[i]]}${j > i ? `–${DLABEL[order[j]]}` : ""} ${t}`);
    i = j + 1;
  }
  return `${parts.join(" · ") || "No hours set"} · ${cfg.slot_duration_minutes || 30}-min slots`;
}
export const initials = (n) => displayName(n || "").split(/[\s,]+/).filter((w) => /^[A-Za-z]/.test(w)).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
export const searchPatients = async (cid, q) => (await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: String(cid), ...(q ? { q } : {}) } })).data?.results ?? [];
export const fetchVisitTypes = async (cid) => (await api.get(`${B}/visit-types`, { params: { client_id: String(cid) } })).data?.visit_types ?? [];
export async function bookAppt(body) { const { data } = await api.post(`${B}/appointments`, { allow_conflict: false, ...body }); return data?.appointment || data; }
export function patientBody(p) {
  return { patient_name: displayName(p.full_name || [p.first_name, p.last_name].filter(Boolean).join(" ")), patient_mrn: p.mrn || undefined, patient_chart_uuid: p.chart_uuid || undefined, patient_chart_id: p.chart_id || undefined,
    patient_dob: p.dob || undefined, patient_gender: p.gender || undefined, patient_phone: p.phone || p.mobile || undefined, patient_email: p.email || undefined };
}
